import { memo, useMemo } from 'react'
import _ from 'lodash'
import { classNames, TruncateText } from '@panwds/react-ui'
import { getAuthState, getFrameworkServiceUrl, getFrameworkVar } from '../../../common/utils/Sparky'
import AppLogo from '../common/AppLogo'
import { showMspPortalInTsg, isInProgressStatus, isRunningStatus } from '../../utils/common'
import { SWITCH_MULTI_TENANT_PORTAL } from '../../../common/constants/LabelConstants'
import { APP_ID_CNGFW_CMS, APP_ID_PRISMA_ACCESS, APP_ID_SCM, GOV_REGION, MSP_URL } from '../../../common/constants/GlobalConstants'
import './MyApps.scss'

import type { VFC, MouseEvent } from 'react'
import type { AppAccessFilterFn, Instance, InstancesAndApps, SingleInstanceWithApp, Tsg } from '../../types'

const preventDefault = (e: MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault()
}

const MSP_PORTAL = 'msp_portal'
const OneApp: VFC<SingleInstanceWithApp> = ({ instance, app }) => {
  const testId = `my-app:${instance.tsg_id}:${instance.app_id}:${instance.tenant_id}`
  const inProgress = isInProgressStatus(instance)
  const url = (!inProgress && instance.url) || '#'
  const onClick = url.length > 1 ? undefined : preventDefault
  const className = classNames('sparky-hub-app', inProgress && 'sparky-hub-app-in-progress')
  const instanceName = useMemo(() => {
    if (!instance.tenant_instance_name || inProgress || instance.app_launch_display_override || instance.app_id_display_override) {
      return null
    }
    const {
      tenant_instance_name,
      tsg_id,
      tsg_name: tsgName = '',
      license_quota_id,
      support_account_id = '',
      support_account_name = '',
      region = '',
      platform_region,
      extra: { allocation_type, session_id, tsg_name = '', platform_id = '' } = {},
    } = instance
    if ((license_quota_id || allocation_type) && !session_id) { // new instances
      return null
    }

    const rg = region === platform_region ? region : `${region}|${platform_region}`
    const regionRegex = new RegExp(`[ -](?:us|eu|${rg})[ -]`, 'i')
    const cleanedAutoName = tenant_instance_name ? tenant_instance_name.toLowerCase()
      .replace(tsgName.toLowerCase(), '').replace(tsg_name.toLowerCase(), '').replace(tsg_id, '')
      .replace(app.display_name?.toLowerCase(), '').replace(regionRegex, '').replace(platform_id, '')
      .replace(support_account_id, '').replace(support_account_name.toLowerCase(), '')
      .replace(/[-_ ]+/g, '').trim() : ''
    if (!cleanedAutoName) {
      return null // skip if the instance name generated by tsg name/id and app display name
    }
    return <TruncateText addClassName='sparky-hub-app-extra-text'>{instance.tenant_instance_name}</TruncateText>
  }, [instance, inProgress, app.display_name])
  return (
    <div className={className} data-testid={testId} data-app-id={instance.app_id} data-display-app-id={app.app_id}>
      <a className='sparky-hub-app-logo-link' href={url} onClick={onClick}>
        <AppLogo src={app.logo || app.normalized_logo_url} alt={app.display_name} />
      </a>
      <a className='sparky-hub-app-name-link' href={url} onClick={onClick}>
        {app.display_name}
        {inProgress && <sub className='sparky-hub-app-extra-text sparky-hub-app-in-progress'>in progress</sub>}
        {instanceName}
      </a>
    </div>
  )
}

const getCspAccountName = (cspId: string | number | undefined) => (cspId ? getAuthState()?.user?.supportAccountIds?.find(acc => +acc.accountid === +cspId)?.name : undefined)

type MyAppsProps = InstancesAndApps & { tsg: Tsg, appAccessFilterFn: AppAccessFilterFn }
const MyApps: VFC<MyAppsProps> = ({ instances, apps, tsg, appAccessFilterFn }) => {
  const showMspPortal = showMspPortalInTsg(tsg, instances)
  const sortedAppInstances = useMemo(() => {
    const scmPathOverride = getFrameworkVar('url_overriding')?.[APP_ID_SCM]
    const mappedInstances = instances.reduce((all, instance) => {
      const app = apps.get(instance.app_id)
      if (app?.enabled && instance && !instance.extra?.hidden && !instance.runtime_attributes?.hidden &&
        ((instance.url && isRunningStatus(instance)) || isInProgressStatus(instance)) &&
        appAccessFilterFn(app) &&
        !app.extension_to && !app.no_app_tile_ui &&
        (app.is_panw ? app.iam_controlled || app.flags?.iam_controlled : app.is_third_party)
      ) {
        const displayAppId = instance.app_launch_display_override || instance.app_id_display_override
        let url = instance.app_launch_url_override || instance.url
        if (!instance.app_launch_url_override && displayAppId === APP_ID_SCM && scmPathOverride) {
          // TODO: remove once framework with app_launch_url_override deployed to prod
          const baseUrl = scmPathOverride.base_url?.startsWith('https:') ?
            scmPathOverride.base_url : getFrameworkServiceUrl(scmPathOverride.base_url)
          const urlObj = new URL(scmPathOverride.default_route || '/home', baseUrl || instance.url)
          urlObj.searchParams.set('tsg_id', tsg.id)
          url = urlObj.toString()
        }
        all.push({
          ...instance,
          support_account_name: instance.support_account_name || getCspAccountName(instance.support_account_id) || '',
          tsg_name: tsg.display_name,
          app: (displayAppId !== instance.app_id && apps.get(displayAppId)) || app,
          url,
        })
      }
      return all
    }, [] as Instance[])
    const fawkesApp = apps.get(APP_ID_PRISMA_ACCESS)
    const scmApp = apps.get(APP_ID_SCM)
    const govRegion = instances.some(inst => ((inst.platform_region || inst.region) === GOV_REGION))
    const isGovSase = govRegion || (instances.length === 0 && tsg?.isSASE)
    const mspUrl = (isGovSase && getFrameworkServiceUrl('msp_portal_gov')) ||
      getFrameworkServiceUrl(MSP_PORTAL) || fawkesApp?.extra?.msp_portal_url || fawkesApp?.extra?.sase_portal_url
    const mspBaseApp = isGovSase ? fawkesApp : scmApp
    if (showMspPortal && mspUrl && mspBaseApp) {
      const url = new URL(MSP_URL, mspUrl)
      url.searchParams.append('tsg_id', tsg.id)
      mappedInstances.push({
        app_id: MSP_PORTAL,
        tsg_id: tsg.id,
        tenant_id: '',
        support_account_id: '',
        url: url.toString(),
        app: {
          ...mspBaseApp,
          app_id: MSP_PORTAL,
          display_name: mspBaseApp?.extra?.multi_tenant_display_name || SWITCH_MULTI_TENANT_PORTAL,
        },
      })
    }
    const cngfwCmsApp = apps.get(APP_ID_CNGFW_CMS)
    if (cngfwCmsApp?.enabled && cngfwCmsApp.app_url) {
      mappedInstances.push({
        app_id: APP_ID_CNGFW_CMS,
        tsg_id: tsg.id,
        tenant_id: '',
        support_account_id: '',
        url: cngfwCmsApp.app_url,
        app: cngfwCmsApp,
      })
    }
    return _.orderBy(mappedInstances, ['app.display_name'])
  }, [tsg.id, tsg.display_name, tsg.isSASE, instances, apps, showMspPortal, appAccessFilterFn])
  const myApps = sortedAppInstances.length ? sortedAppInstances.map(instance => <OneApp
    key={instance.tenant_id || instance.app_id}
    instance={instance}
    app={instance.app}
  />) : tsg.isNone ? 'No tenant is available.' : 'No app instance yet.'

  return <div className='sparky-hub-my-apps' data-testid={`my-apps:${tsg.id}`} data-tsg-id={tsg.id}>{myApps}</div>
}

export default memo(MyApps)
